"use client";
import React, { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { post } from "../../lib/api";
import CryptoJS from "crypto-js";
import { useSidebar } from "../ui/sidebar";
import dendalogo from "../Assets/denda final.png";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [loginError, setLoginError] = useState(false);
  const { setAuthenticated, setUser } = useSidebar();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    setLoading(true);
    const hashedPassword = hashPassword(password);
    const postData = {
      username: username,
      hash: hashedPassword,
    };
    const data = await post(postData, "/login");

    if (data["isValid"]) {
      setAuthenticated(data["isValid"]);
      setUser(data["user"]);
      window.location.reload(); // Force full reload after login
    } else {
      setLoginError(true);
      setTimeout(() => setLoginError(true), 3000); // Reset loginError after 3 seconds
    }
    setLoading(false);
  };

  const hashPassword = (password) => {
    return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
  };
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    let particles = [];
    const particleCount = 50;
    const colors = ["#9333ea", "#a855f7", "#c084fc", "#d8b4fe"];

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 5 + 1;
        this.speedX = Math.random() * 1 - 0.5;
        this.speedY = Math.random() * 1 - 0.5;
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x > canvas.width || this.x < 0) {
          this.speedX = -this.speedX;
        }
        if (this.y > canvas.height || this.y < 0) {
          this.speedY = -this.speedY;
        }
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const init = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, "#f3f4f6");
      gradient.addColorStop(1, "#e5e7eb");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
      }
      drawConnections();
      requestAnimationFrame(animate);
    };

    const drawConnections = () => {
      for (let a = 0; a < particles.length; a++) {
        for (let b = a; b < particles.length; b++) {
          const dx = particles[a].x - particles[b].x;
          const dy = particles[a].y - particles[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(147, 51, 234, ${0.2 - distance / 500})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }
    };

    init();
    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <div className="relative w-screen h-screen grid grid-cols-2">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-10" aria-hidden="true" />
      <div className={"z-10 relative w-full overflow-hidden"}>
        <img src={dendalogo} alt="Denda Logo" className="w-full h-full mx-auto" />

        <div className="text-white text-3xl font-bold absolute top-16 left-16">
          <span className={"text-[2.5rem]"}> Create. </span>
          <br />
          <span className={"text-[2.5rem]"}> Analyze. </span>
          <br />
          <span className={"text-[2.5rem]"}> Optimize. </span>

          <br />
          <span className="text-white/80 text-sm font-light">Empower your insights with the Denda CMC</span>
        </div>

        <div>
          <span className={"text-white/80 text-sm font-light absolute bottom-5 right-10 w-[270px]"}>
            Empower your insights with the Denda CMC - Edit your games, Schedule your content, and challenges, and Capitalise on data points to derive driven insights
          </span>
        </div>
      </div>

      <div className="w-full max-w-2xl z-10 space-y-12 flex items-center justify-center mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md text-gray-800">
          <div className="space-y-2 text-center mb-6">
            <h1 className="text-3xl font-bold text-center">Welcome Back</h1>
            <p className="text-muted-foreground">Enter your credentials to access your account</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 ">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                aria-invalid={error ? "true" : "false"}
                aria-describedby={error ? "login-error" : undefined}
                className="w-full"
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  aria-invalid={error ? "true" : "false"}
                  className="w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {loginError ? (
              <div className={"flex flex-col justify-center items-center space-y-3"}>
                <Button type="submit" onClick={handleLogin} variant="destructive" className="w-full " disabled={loading} aria-busy={loading}>
                  {loading ? "Signing in..." : "Try again"}
                </Button>
                <Label className={" text-red-700"}> Wrong username or password </Label>
              </div>
            ) : (
              <Button
                type="submit"
                onClick={handleLogin}
                className="w-full bg-purple-700 hover:bg-purple-900 dark:bg-purple-700 dark:hover:bg-purple-900 text-white "
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

function EyeIcon(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-10-7-10-7a16.62 16.62 0 0 1 5-5" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
